import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  DatePicker,
  Typography,
  Alert,
  Tag,
  notification,
} from "antd";
import { Trans, useTranslation } from "react-i18next";
import moment from "moment";
import Colors from "../../../../../themes/Colors";
import { syncRulesWithCampaignDates } from "../../../../../services/utils";
import ChangeDatesNoticeModal from "./ChangeDatesNoticeModal";

const { RangePicker } = DatePicker;

const EditDatesModal = ({
  visible,
  campaign,
  editCampaignDate,
  isEditingCampaignDate,
  errors,
  onCancel,
}) => {
  const { t } = useTranslation();
  const primaryStyle = {
    background: Colors.primary,
    borderColor: Colors.primary,
    color: Colors.onPrimary,
  };
  const [step, setStep] = useState(1);
  const [dates, setDates] = useState({ start_date: null, end_date: null });
  const [originalDates, setOriginalDates] = useState({
    start_date: null,
    end_date: null,
  });
  const [previousIsEditing, setPreviousIsEditing] = useState(false);
  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);

  useEffect(() => {
    if (campaign) {
      const campaignDates = {
        start_date: campaign.start_date
          ? moment(campaign.start_date).format("YYYY-MM-DD")
          : "2025-01-01",
        end_date: campaign.end_date
          ? moment(campaign.end_date).format("YYYY-MM-DD")
          : "2025-01-01",
      };
      setDates(campaignDates);
      setOriginalDates(campaignDates);
    }
  }, [campaign]);

  useEffect(() => {
    if (visible) setStep(1);
  }, [visible]);

  useEffect(() => {
    if (previousIsEditing && !isEditingCampaignDate) {
      if (errors && errors.length > 0) {
        const converted = errors[0].replace(
          "Error updating campaign dates: ",
          ""
        );
        notification.error({
          message: t("Error"),
          description: t(converted),
          placement: "topRight",
        });
        setStep(1);
      } else {
        notification.success({
          message: t("Campaign successfully edited"),
          description: t("Campaign dates updated successfully"),
          placement: "topRight",
        });
        onCancel();
        setStep(1);
      }
    }
    setPreviousIsEditing(isEditingCampaignDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingCampaignDate, errors, t]);

  const handleDateChange = (value) => {
    if (value[1] < moment().startOf("day")) {
      notification.error({
        message: t("Error"),
        description: t("The start date must be greater than today"),
        placement: "topRight",
      });
      return;
    }
    setDates({
      start_date: value[0]
        ? value[0].format("YYYY-MM-DD")
        : originalDates.start_date,
      end_date: value[1] ? value[1].format("YYYY-MM-DD") : originalDates.end_date,
    });
  };

  const disabledDate = (current) =>
    current && current < moment().startOf("day");

  const handleConfirm = () => {
    const { updatedContents, rulesCount } = syncRulesWithCampaignDates(
      campaign.contents_displays,
      dates.start_date,
      dates.end_date
    );
    const contentsToSave = updatedContents.map((item) => ({
      ...item,
      relation_id: item.id,
    }));
    editCampaignDate(campaign.id, {
      name: campaign.name,
      start_date: dates.start_date,
      end_date: dates.end_date,
      is_test: campaign.is_test,
      contents_displays: contentsToSave,
    });
    if (rulesCount > 0) {
      setIsSyncModalVisible(true);
    }
  };

  const handleCancel = () => {
    setStep(1);
    setDates(originalDates);
    onCancel();
  };

  if (!campaign) return null;

  const isPending = campaign.payment_status === "pending";

  const footer = [];
  if (step === 1 && !isPending) {
    footer.push(
      <Button key="cancel" onClick={handleCancel}>
        {t("Cancel")}
      </Button>
    );
    footer.push(
      <Button
        key="next"
        type="primary"
        style={primaryStyle}
        onClick={() => setStep(2)}
        disabled={
          !dates.start_date ||
          !dates.end_date ||
          (dates.start_date === originalDates.start_date &&
            dates.end_date === originalDates.end_date)
        }
      >
        {t("Continue")}
      </Button>
    );
  } else if (step === 2 && !isPending) {
    footer.push(
      <Button key="back" onClick={() => setStep(1)}>
        {t("Go back")}
      </Button>
    );
    footer.push(
      <Button key="cancel" onClick={handleCancel}>
        {t("Cancel")}
      </Button>
    );
    footer.push(
      <Button
        key="confirm"
        type="primary"
        style={primaryStyle}
        onClick={handleConfirm}
        loading={isEditingCampaignDate}
      >
        {t("Apply")}
      </Button>
    );
  } else if (isPending) {
    footer.push(
      <Button key="cancel" onClick={handleCancel}>
        {t("Cancel")}
      </Button>
    );
    footer.push(
      <Button
        key="confirm"
        type="primary"
        style={primaryStyle}
        onClick={handleConfirm}
        loading={isEditingCampaignDate}
      >
        {t("Apply")}
      </Button>
    );
  }

  return (
    <>
      <Modal
        title={t("Edit exhibition dates")}
        visible={visible}
        onCancel={handleCancel}
        footer={footer}
      >
        {step === 1 ? (
          <div style={{ margin: "25px 40px 30px" }}>
            <div style={{ marginBottom: 8 }}>{t("Exhibition date")}</div>
            <RangePicker
              style={{ width: "100%" }}
              disabledDate={disabledDate}
              value={[moment(dates.start_date), moment(dates.end_date)]}
              dropdownAlign={{ offset: [0, 8] }}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
            />
          </div>
        ) : (
          <div style={{ margin: "25px 40px 30px" }}>
            <Alert
              message={t("Edit campaign dates warning title")}
              description={
                <>
                  <Typography type="secondary">{t("New dates")}</Typography>
                  <Tag color={Colors.atention}>
                    {moment(dates.start_date).format("DD/MM/YYYY")} -{" "}
                    {moment(dates.end_date).format("DD/MM/YYYY")}
                  </Tag>
                  <br />
                  <br />
                  <Trans
                    i18nKey="Edit campaign dates warning"
                    components={{ tag: <strong /> }}
                  />
                </>
              }
              type="warning"
              showIcon
            />
          </div>
        )}
      </Modal>
      <ChangeDatesNoticeModal
        visible={isSyncModalVisible}
        onCancel={() => setIsSyncModalVisible(false)}
      />
    </>
  );
};

export default EditDatesModal;
